import { Search } from "@mui/icons-material";
import { List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import { Form, Outlet, useFormAction, useLoaderData, useParams, useSubmit } from "react-router-dom";
import { Inventory } from "types/inventory";
import { PagePaperPreamble } from "./Layouts/components/PagePaperPreamble";
import { PageBreadcrumbs, PageTitle } from "./Layouts/components/PageTitleContainer";
import { FullPagePaperLayout } from "./Layouts/FullPagePaperLayout";
import { warehouses } from "../constants/warehouses";

export function ReplacementFinderPanel() {
  const { inventory } = useLoaderData() as { inventory: Inventory };

  return (
    <Stack direction="row" height="100%">
      <Stack padding={2}>
        <Typography variant="h6">Current Inventory</Typography>
        <List dense>
          <ListItem divider>
            <ListItemText primary="Label" secondary={inventory.label || "(empty)"} />
          </ListItem>
          <ListItem divider>
            <ListItemText primary="SKU" secondary={inventory.sku || "(empty)"} />
          </ListItem>
          <ListItem divider>
            <ListItemText primary="Model" secondary={inventory.model || "(empty)"} />
          </ListItem>
          <ListItem divider>
            <ListItemText primary="Model order" secondary={inventory.modelOrder || "(empty)"} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Keyboard language" secondary={inventory.keyboardLanguage || "(empty)"} />
          </ListItem>
        </List>
      </Stack>
      {inventory.sku ? (
        <Stack flexGrow={1} padding={2}>
          <Typography variant="h6">Replacement Inventories</Typography>
          <RevivnDatabaseDataGrid
            autoHeight
            url={`/inventory_replacements/${inventory.sku}`}
            columns={[
              { field: "id", headerName: "ID", type: "string", minWidth: 100, sortable: false, filterable: false },
              { field: "label", headerName: "Label", minWidth: 100, sortable: false, filterable: false },
              {
                field: "warehouseId",
                headerName: "Warehouse",
                minWidth: 100,
                renderCell: (params) =>
                  warehouses.find((warehouse) => warehouse.id == params.value)?.acronym ?? params.value,
                sortable: false,
                filterable: true,
              },
              {
                field: "warehouseLocation",
                headerName: "Location",
                minWidth: 200,
                sortable: false,
                filterable: true,
                renderCell: ({ row }) => (
                  <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationId} />
                ),
              },
              { field: "state", headerName: "State", minWidth: 100, sortable: false, filterable: false },
              { field: "sku", headerName: "SKU", minWidth: 150, sortable: false, filterable: false },
              { field: "model", headerName: "Model", minWidth: 200, sortable: false, filterable: false },
              { field: "modelOrder", headerName: "Model Order", minWidth: 150, sortable: false, filterable: false },
              { field: "keyboardLanguage", headerName: "Language", minWidth: 150, sortable: false, filterable: false },
              { field: "comments", headerName: "Comments", minWidth: 300, sortable: false, filterable: false },
            ]}
            componentsProps={{
              toolbar: { sx: { display: "none" } },
            }}
            sx={{ marginTop: 1 }}
          />
        </Stack>
      ) : (
        <Stack flexGrow={1} padding={2}>
          <Typography color="text.secondary" variant="caption">
            Cannot fetch replacements when inventory does not have a SKU.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

export function ReplacementFinderPage() {
  const { inventoryId } = useParams();
  const action = useFormAction();
  const submit = useSubmit();

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Returns" }, { label: "Replacement Finder" }]} />
      <PageTitle>Replacement Finder</PageTitle>
      <PagePaperPreamble>
        <Stack spacing={1}>
          <Form method="POST">
            <TextField
              autoFocus
              label="Inventory Label"
              name="id"
              defaultValue={inventoryId}
              onChange={(e) => {
                if (e.target.value.trim() === "") submit({ id: "" }, { action: action });
              }}
              placeholder="Scan label"
              size="small"
              slotProps={{
                input: { startAdornment: <Search fontSize="small" /> },
              }}
              sx={{ marginTop: 1 }}
            />
            <input hidden type="submit" />
          </Form>
          <Typography color="text.secondary" variant="caption">
            Results are automatically sorted by closest match to the inventory and filtered to your warehouse.
          </Typography>
        </Stack>
      </PagePaperPreamble>
      <Outlet />
    </FullPagePaperLayout>
  );
}
